import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PolicyTypeInput } from "./types";

export const usePolicyTypeOperations = () => {
  const queryClient = useQueryClient();

  const createPolicyType = async (data: PolicyTypeInput) => {
    const { data: newPolicyType, error } = await supabase
      .from('policy_types')
      .insert([{
        name: data.name,
        description: data.description,
        sort_order: data.sort_order,
        policy_line_id: data.policy_line_id,
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating policy type:', error);
      return { success: false, error };
    }

    // Invalidate both policyTypes and policyLines queries
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['policyTypes'] }),
      queryClient.invalidateQueries({ queryKey: ['policyLines'] })
    ]);
    
    return { success: true, data: newPolicyType };
  };

  const updatePolicyType = async (id: string, data: PolicyTypeInput) => {
    const { data: updatedPolicyType, error } = await supabase
      .from('policy_types')
      .update({
        name: data.name,
        description: data.description,
        sort_order: data.sort_order,
        policy_line_id: data.policy_line_id,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating policy type:', error);
      return { success: false, error };
    }

    // Invalidate both policyTypes and policyLines queries
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['policyTypes'] }),
      queryClient.invalidateQueries({ queryKey: ['policyLines'] })
    ]);

    return { success: true, data: updatedPolicyType };
  };

  const deletePolicyType = async (id: string) => {
    const { error } = await supabase
      .from('policy_types')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting policy type:', error);
      return { success: false, error };
    }

    // Invalidate both policyTypes and policyLines queries
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['policyTypes'] }),
      queryClient.invalidateQueries({ queryKey: ['policyLines'] })
    ]);

    return { success: true };
  };

  return {
    createPolicyType,
    updatePolicyType,
    deletePolicyType,
  };
};